import React, { useEffect, useState, useMemo } from "react";
import { useParams, NavLink, Navigate, useNavigate } from "react-router-dom";
import { listRules } from "../lib/api";
import { Shield, ChevronRight, Loader2 } from "lucide-react";

function renderContent(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let listBuf = [];
  const flushList = (key) => {
    if (listBuf.length) {
      out.push(
        <ul key={`ul-${key}`} className="list-disc pl-6 my-3 space-y-1.5 text-white/80">
          {listBuf.map((li, i) => (
            <li key={i}>{li}</li>
          ))}
        </ul>
      );
      listBuf = [];
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (line.startsWith("## ")) {
      flushList(idx);
      out.push(
        <h3
          key={`h-${idx}`}
          className="font-display uppercase tracking-wider text-xl text-[#FF5E00] mt-8 mb-3"
        >
          {line.slice(3)}
        </h3>
      );
    } else if (line.startsWith("- ")) {
      listBuf.push(line.slice(2));
    } else if (line.trim() === "") {
      flushList(idx);
      out.push(<div key={`sp-${idx}`} className="h-2" />);
    } else {
      flushList(idx);
      out.push(
        <p key={`p-${idx}`} className="text-white/80 leading-relaxed my-2">
          {line}
        </p>
      );
    }
  });
  flushList("end");
  return out;
}

export default function Rules() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const { slug } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    listRules()
      .then((d) => setRules(d))
      .finally(() => setLoading(false));
  }, []);

  const active = useMemo(
    () => rules.find((r) => r.slug === slug) || rules[0],
    [rules, slug]
  );

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#FF5E00]" />
      </div>
    );
  }

  if (!loading && rules.length && !slug) {
    return <Navigate to={`/rules/${rules[0].slug}`} replace />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="rules-page">
      <div className="mb-10">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#9D4CDD]/40 bg-[#9D4CDD]/10 text-[#B366F0] text-xs uppercase tracking-[0.25em] font-display mb-4">
          <Shield className="w-3 h-3" /> Rulebook
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight">
          The <span className="text-gradient-orange">Rules</span>
        </h1>
        <p className="mt-3 text-white/65 max-w-2xl">
          Everything you need to compete clean. Navigate sections from the sidebar.
        </p>
      </div>

      <div className="grid lg:grid-cols-[280px_1fr] gap-8">
        {/* SIDEBAR */}
        <aside className="lg:sticky lg:top-20 self-start">
          <select
            className="lg:hidden w-full bg-[#0A0512] border border-[#9D4CDD]/40 text-white rounded-md px-4 py-3 mb-4 font-display uppercase tracking-wider text-sm"
            value={active?.slug || ""}
            onChange={(e) => navigate(`/rules/${e.target.value}`)}
            data-testid="rules-mobile-select"
          >
            {rules.map((r) => (
              <option key={r.id} value={r.slug}>
                {r.title}
              </option>
            ))}
          </select>
          <nav className="hidden lg:block glass-card rounded-xl p-3">
            {rules.map((r) => (
              <NavLink
                key={r.id}
                to={`/rules/${r.slug}`}
                className={({ isActive }) =>
                  `flex items-center justify-between gap-2 px-4 py-3 rounded-md font-display text-xs uppercase tracking-[0.15em] transition-all ${
                    isActive
                      ? "bg-[#FF5E00] text-black font-bold"
                      : "text-white/70 hover:bg-[#9D4CDD]/15 hover:text-white"
                  }`
                }
                data-testid={`rules-nav-${r.slug}`}
              >
                <span>{r.title}</span>
                <ChevronRight className="w-4 h-4 opacity-60" />
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* CONTENT */}
        <article className="glass-card rounded-xl p-6 sm:p-10" data-testid="rules-content">
          {active ? (
            <>
              <div className="text-xs uppercase tracking-[0.3em] text-[#9D4CDD] font-display">
                Section {active.order}
              </div>
              <h2 className="font-display uppercase font-black text-3xl sm:text-4xl tracking-tight mt-2 mb-6">
                {active.title}
              </h2>
              <div className="prose prose-invert max-w-none">
                {renderContent(active.content)}
              </div>
            </>
          ) : (
            <p className="text-white/60">No rules published yet.</p>
          )}
        </article>
      </div>
    </div>
  );
}
